// Client-side image normalization before upload.
//
// Why: iPhone photos are HEIC, which WKWebView frequently can't display through
// a blob <img> (you get a broken-image glyph); camera shots are also large and
// carry EXIF orientation. Re-encoding to a downscaled JPEG fixes all three and
// hands the server clean, universally-renderable bytes. Non-images — and images
// we can't decode (e.g. HEIC on an older engine) — pass through untouched so
// nothing is ever lost.

export type PreparedUpload = { blob: Blob; filename: string; mimeType: string }

const MAX_IMAGE_DIM = 2048
const JPEG_QUALITY = 0.85

// Formats every target browser renders directly. These pass through untouched —
// re-encoding a PNG screenshot to JPEG would soften its text for no benefit. We
// only transcode formats the WebView can't reliably show (chiefly iOS HEIC).
const WEB_SAFE = /^image\/(png|jpe?g|gif|webp|avif|bmp)$/i

export async function normalizeUpload(file: File): Promise<PreparedUpload> {
  const passthrough: PreparedUpload = {
    blob: file,
    filename: file.name,
    mimeType: file.type || 'application/octet-stream',
  }
  // Leave non-images and already-web-safe images alone; only transcode the rest
  // (HEIC/HEIF) to JPEG.
  if (!file.type.startsWith('image/') || WEB_SAFE.test(file.type)) return passthrough

  try {
    const { source, width, height, bitmap } = await decodeImage(file)
    const scale = Math.min(1, MAX_IMAGE_DIM / Math.max(width, height))
    const w = Math.max(1, Math.round(width * scale))
    const h = Math.max(1, Math.round(height * scale))

    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('no 2d context')
    ctx.drawImage(source, 0, 0, w, h)
    bitmap?.close?.()

    const blob = await new Promise<Blob | null>((res) =>
      canvas.toBlob(res, 'image/jpeg', JPEG_QUALITY),
    )
    if (!blob) throw new Error('toBlob returned null')

    return {
      blob,
      filename: file.name.replace(/\.[^./\\]+$/, '') + '.jpg',
      mimeType: 'image/jpeg',
    }
  } catch {
    // Undecodable here (rare) — upload the original so the file still sends.
    return passthrough
  }
}

type Decoded = {
  source: CanvasImageSource
  width: number
  height: number
  bitmap?: ImageBitmap
}

// Prefer createImageBitmap (honors EXIF orientation via imageOrientation); fall
// back to an <img> decode where createImageBitmap is unavailable or rejects.
async function decodeImage(file: File): Promise<Decoded> {
  if (typeof createImageBitmap === 'function') {
    try {
      const bm = await createImageBitmap(file, {
        imageOrientation: 'from-image',
      } as ImageBitmapOptions)
      return { source: bm, width: bm.width, height: bm.height, bitmap: bm }
    } catch {
      // fall through to the <img> path
    }
  }
  const url = URL.createObjectURL(file)
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image()
      el.onload = () => resolve(el)
      el.onerror = () => reject(new Error('image decode failed'))
      el.src = url
    })
    return { source: img, width: img.naturalWidth, height: img.naturalHeight }
  } finally {
    URL.revokeObjectURL(url)
  }
}
